/* assets/appointments-timeline.js
   Flawless Carat – Appointment Scheduler (AJAX -> /contact, no navigation)
   - Posts URL-encoded payload to /contact (Shopify contact endpoint)
   - Success stays on page and shows confirmation
   - Works even if your contact page is /pages/contact-us (that's only for anchors)
*/
;(() => {
  "use strict";

  // Helper: pretty time like "1:00 PM" (FORCED 12-HOUR)
  const prettyTime = (t) => {
    try {
      const [h, m] = t.split(":").map(n => parseInt(n, 10));
      const d = new Date();
      d.setHours(h, m || 0, 0, 0);
      return d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return t;
    }
  };

  class AppointmentTimeline extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this._open = false;
      this._lastFocus = null;
      this.state = {
        step: 1,
        location: "In Studio — NYC",
        service: "",
        durationText: "",
        date: "",
        time: "",
      };
    }

    /* ---------- attributes ---------- */
    // Visuals
    get accentColor()  { return this.getAttribute("accent-color") || "#1b686e"; }
    get goldColor()    { return this.getAttribute("gold-color")   || "#c9a72d"; }
    get bgColor()      { return this.getAttribute("bg-color")     || "#a6dadf"; }
    // Optional assets
    get logoSrc()      { return this.getAttribute("logo-src")     || ""; }
    get inspireSrc()   { return this.getAttribute("inspire-src")  || ""; }
    get designSrc()    { return this.getAttribute("design-src")   || ""; }
    get setSrc()       { return this.getAttribute("set-src")      || ""; }
    get deliverSrc()   { return this.getAttribute("deliver-src")  || ""; }
    // UX
    get requirePhone() { return this.hasAttribute("require-phone"); }
    get successText()  { return this.getAttribute("success-text") || "Thanks — we’ll confirm your appointment shortly."; }
    get errorText()    { return this.getAttribute("error-text")   || "Something went wrong — please try again."; }
    // Nice URL to reference (NOT used for POST)
    get contactUrl()   { return this.getAttribute("contact-url")  || "/contact#contact_form"; }

    connectedCallback() {
      this.render();
      this.setupScheduler();
    }

    /* ---------- render UI ---------- */
    render() {
      const styles = `
        <style>
          :host{
            --accent:${this.accentColor};
            --gold:${this.goldColor};
            --page-bg:${this.bgColor};
            --font-ui: var(--appointments-font, 'Perpetua', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif);
            --header-offset: var(--header-height, 80px);
            display:block; position:relative; z-index:0;
          }
          *,*::before,*::after{ box-sizing:border-box; }

          .wrap{ background:var(--page-bg); padding-top:var(--header-offset); }

          /* a11y */
          .sr-only{ position:absolute!important; left:-9999px!important; width:1px!important; height:1px!important; opacity:0!important; pointer-events:none!important; }

          /* HERO */
          .hero{
            min-height:min(90vh, 960px);
            display:grid; place-items:center; text-align:center;
            padding:1rem 1.25rem 4rem;
            row-gap:.2rem;
          }
          .hero h1{
            font-family:var(--font-ui);
            font-size:clamp(2.25rem,4.6vw,3.5rem);
            font-weight:700; letter-spacing:.02em;
            color:var(--accent);
            margin:0 0 .15rem;
            line-height:1.12;
          }
          .hero p{
            color:#13484c; opacity:.85; max-width:48rem;
            font-size:clamp(1rem,1.5vw,1.125rem);
            line-height:1.45; margin:0 0 .35rem;
          }
          .cta{
            appearance:none; border:0; cursor:pointer;
            background:var(--accent); color:#fff; font-weight:800;
            padding:1.1rem 1.6rem; min-width:260px;
            border-radius:10px; letter-spacing:.06em; text-transform:uppercase;
            box-shadow:0 16px 32px -16px rgba(0,0,0,.35);
            transition:transform .18s ease, background .18s ease;
          }
          .cta:hover{ background:var(--gold); transform:translateY(-1px); }

          /* TIMELINE CARDS */
          .timeline{ display:grid; gap:3.2rem; padding:2rem 1rem 6rem; max-width:1200px; margin:0 auto; }
          .card{
            display:grid; gap:1.2rem; padding:1.2rem; border-radius:16px;
            background:linear-gradient(180deg, rgba(255,255,255,.62), rgba(255,255,255,.38));
            border:1px solid rgba(255,255,255,.65);
            backdrop-filter:blur(26px) saturate(120%);
            -webkit-backdrop-filter:blur(26px) saturate(120%);
            box-shadow:inset 0 1px 0 rgba(255,255,255,.5), 0 25px 60px -22px rgba(0,0,0,.35);
          }
          .card__media{ position:relative; border-radius:12px; overflow:hidden; aspect-ratio:16/9; }
          .card__media img{ width:100%; height:100%; object-fit:cover; display:block; transform:scale(1.02); transition:transform .6s ease; }
          .card:hover .card__media img{ transform:scale(1.06); }
          .card h2{ font-family:var(--font-ui); font-size:clamp(1.35rem,2.6vw,1.8rem); color:var(--accent); margin:.2rem 0; }
          .card p{ color:#0f383c; opacity:.85; line-height:1.65; font-size:clamp(1rem,1.2vw,1.0625rem); }
          .step{ font-size:.85rem; text-transform:uppercase; letter-spacing:.12em; color:#0f383c; opacity:.7; }
          @media(min-width:900px){
            .card{ grid-template-columns:1.2fr 1fr; align-items:center; }
            .card.card--reverse{ grid-template-columns:1fr 1.2fr; }
          }

          /* SHEET (modal) */
          .sheet{ position:fixed; inset:0; z-index:50; display:none; align-items:center; justify-content:center; }
          .sheet[open]{ display:flex; }
          .backdrop{ position:absolute; inset:0; background:rgba(0,0,0,.45); opacity:0; transition:opacity .25s ease; }
          .sheet[open] .backdrop{ opacity:1; }
          .panel{
            position:relative; width:min(100%, 1000px); max-height:92vh; border-radius:16px;
            background:linear-gradient(180deg, rgba(255,255,255,.65), rgba(255,255,255,.42));
            border:1px solid rgba(255,255,255,.7);
            backdrop-filter:blur(28px) saturate(130%);
            -webkit-backdrop-filter:blur(28px) saturate(130%);
            box-shadow: inset 0 1px 0 rgba(255,255,255,.5), 0 30px 80px -30px rgba(0,0,0,.45);
            transform:translateY(8%); opacity:0; transition:transform .28s ease, opacity .28s ease;
          }
          .sheet[open] .panel{ transform:translateY(0); opacity:1; }
          .ph{ position:relative; display:flex; align-items:center; justify-content:center; padding:1rem 1.25rem .75rem; }
          .title{ font-family:var(--font-ui); color:var(--accent); font-weight:800; letter-spacing:.04em; font-size:1.5rem; text-align:center; }
          .x{ position:absolute; right:12px; top:8px; appearance:none; border:0; background:transparent; font-size:24px; line-height:1; cursor:pointer; color:#0f383c; opacity:.85; }
          .content{ padding:0 1.25rem 1.25rem; overflow:auto; max-height:calc(92vh - 60px); }

          /* STEPPER */
          .stepper{ display:grid; grid-template-columns:repeat(4,1fr); gap:.5rem; align-items:center; padding:.5rem 0 1rem; }
          .stepper__item{ position:relative; text-align:center; font-weight:700; font-size:.85rem; letter-spacing:.08em; color:#0f383c; opacity:.65; }
          .stepper__item::before{ content:''; display:block; margin:0 auto .35rem; width:24px; height:24px; border-radius:50%; background:#dfeff0; border:2px solid #bcd7d9; }
          .stepper__item.active{ opacity:1; color:var(--accent); }
          .stepper__item.active::before{ background:var(--gold); border-color:#8b7320; }
          .stepper__line{ grid-column:1/-1; height:2px; background:linear-gradient(90deg, var(--gold) 0%, #cbdedf 0%); }

          /* PANES */
          .pane{ display:none; } .pane[active]{ display:block; }

          /* LOCATION – centered column with icon above text */
          .loc-grid{ display:grid; grid-template-columns:1fr; gap:1rem; }
          @media(min-width:700px){ .loc-grid{ grid-template-columns:1fr 1fr; } }
          .loc{
            border:1px solid rgba(0,0,0,.08);
            background:rgba(255,255,255,.96);
            border-radius:10px; padding:1rem;
            display:flex; flex-direction:column; align-items:center; justify-content:center; gap:.5rem;
            text-align:center; cursor:pointer; transition:background .2s ease, outline .2s ease, transform .18s ease;
          }
          .loc svg{ width:36px; height:36px; flex:none; }
          .loc:hover{ transform:translateY(-1px); }
          .loc[aria-pressed="true"]{ outline:3px solid var(--gold); background:linear-gradient(180deg,#fff,#fff8e0); }

          /* SERVICES – centered */
          .svc-list{ display:grid; grid-template-columns:1fr; gap:.85rem; }
          @media(min-width:700px){ .svc-list{ grid-template-columns:1fr 1fr; } }
          .svc{
            position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;
            gap:.35rem; padding:1rem .75rem; border-radius:12px;
            border:1px solid rgba(0,0,0,.08); background:rgba(255,255,255,.98);
            cursor:pointer; transition:background .18s ease, box-shadow .18s ease, transform .18s ease, border-color .18s ease;
          }
          .svc:hover{ background:#fffef6; border-color:var(--gold); box-shadow:0 8px 20px rgba(0,0,0,.08); transform:translateY(-1px); }
          .svc .t{ font-weight:800; color:#0f383c; font-size:1.15rem; letter-spacing:.01em; }
          .svc .d{ font-size:.98rem; color:#0f383c; opacity:.75; line-height:1.4; }
          .pill{ display:inline-block; background:#ecf6f6; border:1px solid #c9e5e5; border-radius:999px; padding:.25rem .6rem; font-size:1rem; font-weight:800; color:#0f383c; }
          .svc.selected{ outline:3px solid var(--gold); box-shadow:0 12px 26px rgba(0,0,0,.12); }
          .svc.selected::after{
            content:'✓'; position:absolute; top:10px; right:10px; width:26px; height:26px; border-radius:50%; background:var(--gold);
            color:#111; display:grid; place-items:center; font-weight:900; font-size:14px;
          }

          /* DATE & TIME – clean grid, blue slots w/ gold hover/selected */
          .row{ display:grid; grid-template-columns:1fr; gap:1rem; }
          @media(min-width:700px){ .row--2{ grid-template-columns:1fr 1fr; } }
          label{ font-weight:700; letter-spacing:.02em; color:#0f383c; font-size:.98rem; }
          input,select,textarea{
            width:100%; padding:.8rem 1rem; border:1px solid rgba(0,0,0,.06); border-radius:10px; background:#fff; font-family:inherit;
          }
          .tray{ display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:12px; padding:0; background:transparent; border:0; box-shadow:none; }
          .slot{
            appearance:none; border:1px solid #86c7cd; border-radius:10px; padding:14px 10px;
            background:#a6dadf; color:#0f383c; font-weight:800; cursor:pointer;
            box-shadow:0 6px 14px rgba(0,0,0,.12);
            transition:transform .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease;
          }
          .slot:hover,.slot.selected{ background:var(--gold); color:#1b1b1b; box-shadow:0 10px 20px rgba(0,0,0,.18); }

          /* CONFIRM + STATUS */
          textarea{ min-height:110px; resize:vertical; }
          .summary{ background:rgba(255,255,255,.96); border:1px solid rgba(0,0,0,.06); border-radius:10px; padding:.75rem; }
          .sum{ display:flex; gap:.5rem; align-items:center; color:#0f383c; }
          .sum strong{ min-width:6.5rem; }
          .actions{ display:flex; justify-content:space-between; gap:.5rem; margin-top:1rem; }
          .btn{ appearance:none; border:0; border-radius:10px; padding:.85rem 1rem; font-weight:800; letter-spacing:.06em; text-transform:uppercase; cursor:pointer; transition:background .18s ease, transform .18s ease; }
          .btn--ghost{ background:#fff; border:1px solid rgba(0,0,0,.12); color:#0f383c; }
          .btn--fill{ background:var(--accent); color:#fff; box-shadow:0 10px 22px -10px rgba(0,0,0,.35); }
          .btn--fill:hover, .btn--ghost:hover{ background:var(--gold); color:#1b1b1b; transform:translateY(-1px); }
          .status{ margin-top:.75rem; padding:.8rem; border-radius:10px; text-align:center; font-weight:700; }
          .ok{ background:rgba(34,197,94,.12); border:1px solid rgba(34,197,94,.35); color:#166534; }
          .err{ background:rgba(239,68,68,.12); border:1px solid rgba(239,68,68,.35); color:#991b1b; }
        </style>
      `;

      const heroLogo = this.logoSrc
        ? `<img src="${this.logoSrc}" alt="Logo" style="height:300px;object-fit:contain;opacity:.9;margin-bottom:1rem" />`
        : "";

      const timeline = `
        <section class="timeline">
          <article class="card">
            <div class="card__media">${this.inspireSrc ? `<img loading="lazy" src="${this.inspireSrc}" alt="Inspiration mood">` : ""}</div>
            <div><div class="step">Step 1/4 — Inspire</div><h2>Share your inspiration — we’ll create together</h2><p>A single hint is enough to start your story.</p></div>
          </article>
          <article class="card card--reverse">
            <div><div class="step">Step 2/4 — Design</div><h2>3D rendering will be provided, designed to your taste</h2><p>You'll have the opportunity to see multidimensional images.</p></div>
            <div class="card__media">${this.designSrc ? `<img loading="lazy" src="${this.designSrc}" alt="Design process">` : ""}</div>
          </article>
          <article class="card">
            <div class="card__media">${this.setSrc ? `<img loading="lazy" src="${this.setSrc}" alt="Setting and craftsmanship">` : ""}</div>
            <div><div class="step">Step 3/4 — Set</div><h2>We craft each and every design to your specifications with the utmost precision and quality.</h2><p>Always ethically sourced materials. We obsess the details so you don’t have to.</p></div>
          </article>
          <article class="card card--reverse">
            <div><div class="step">Step 4/4 — Deliver</div><h2>Insured delivery or studio pickup — your choice</h2><p>Typical timelines run 2–3 weeks. We can provide you with your target date from our initial consultation.</p></div>
            <div class="card__media">${this.deliverSrc ? `<img loading="lazy" src="${this.deliverSrc}" alt="Velvet box delivery">` : ""}</div>
          </article>
        </section>
      `;

      const services = [
        { t: "Engagement Ring", d: "Find your ideal setting and center stone", dur: "45 minutes–1 hour" },
        { t: "Wedding Rings", d: "Select the perfect wedding bands", dur: "45 minutes–1 hour" },
        { t: "Fine Jewelry & Gifts", d: "Necklaces, bracelets, earrings & more", dur: "45 minutes–1 hour" },
        { t: "Pick Up / Drop Off — Sizing / Service / Repairs", d: "Leave or collect your item for servicing" },
      ];

      const sheet = `
        <div class="sheet" id="scheduler" role="dialog" aria-modal="true" aria-labelledby="schedTitle">
          <div class="backdrop" data-close></div>
          <div class="panel">
            <div class="ph">
              <div class="title" id="schedTitle">Schedule Your Appointment</div>
              <button class="x" type="button" data-close aria-label="Close">×</button>
            </div>
            <div class="content">
              <div class="stepper">
                <div class="stepper__item" data-k="1">LOCATION</div>
                <div class="stepper__item" data-k="2">SERVICE</div>
                <div class="stepper__item" data-k="3">DATE & TIME</div>
                <div class="stepper__item" data-k="4">CONFIRM</div>
                <div class="stepper__line"></div>
              </div>

              <!-- 1: Location -->
              <section class="pane" data-step="1" active>
                <div class="loc-grid">
                  <button type="button" class="loc" data-loc="In Studio — NYC" aria-pressed="true">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 22s8-6.2 8-12a8 8 0 10-16 0c0 5.8 8 12 8 12z" stroke="#0f383c" stroke-width="1.5"/><circle cx="12" cy="10" r="3" stroke="#0f383c" stroke-width="1.5"/></svg>
                    <div><strong>In Studio — NYC</strong><div style="opacity:.7;font-size:.95rem">Diamond District • 45–60 minute sessions</div></div>
                  </button>
                  <button type="button" class="loc" data-loc="Virtual" aria-pressed="false">
                    <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="12" rx="2" stroke="#0f383c" stroke-width="1.5"/><path d="M8 20h8" stroke="#0f383c" stroke-width="1.5"/></svg>
                    <div><strong>Virtual</strong><div style="opacity:.7;font-size:.95rem">Zoom or FaceTime • 45–60 minutes</div></div>
                  </button>
                </div>
                <div class="actions">
                  <button class="btn btn--ghost" disabled>Back</button>
                  <button class="btn btn--fill js-next" type="button">Next</button>
                </div>
              </section>

              <!-- 2: Service -->
              <section class="pane" data-step="2">
                <div class="svc-list">
                  ${services.map(s => `
                    <button type="button" class="svc" data-svc="${s.t}" data-dur="${s.dur || ''}">
                      <div class="t">${s.t}</div>
                      ${s.dur ? `<div class="pill">${s.dur}</div>` : ``}
                      <div class="d">${s.d}</div>
                    </button>
                  `).join("")}
                </div>
                <div class="actions">
                  <button class="btn btn--ghost js-back" type="button">Back</button>
                  <button class="btn btn--fill js-next" type="button" disabled>Next</button>
                </div>
              </section>

              <!-- 3: Date & Time -->
              <section class="pane" data-step="3">
                <div class="row row--2">
                  <div>
                    <label for="sched_date">Preferred date</label>
                    <input id="sched_date" type="date">
                  </div>
                  <div>
                    <label>Preferred time</label>
                    <div class="tray">
                      ${["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"]
                        .map(t => `<button type="button" class="slot" data-time="${t}">${prettyTime(t)}</button>`).join("")}
                    </div>
                  </div>
                </div>
                <div class="actions">
                  <button class="btn btn--ghost js-back" type="button">Back</button>
                  <button class="btn btn--fill js-next" type="button" disabled>Next</button>
                </div>
              </section>

              <!-- 4: Confirm -->
              <section class="pane" data-step="4">
                <div class="summary" style="margin-bottom:1rem">
                  <div class="sum"><strong>Location</strong><span class="sum-loc"></span></div>
                  <div class="sum"><strong>Service</strong><span class="sum-svc"></span></div>
                  <div class="sum"><strong>When</strong><span class="sum-dt"></span></div>
                </div>

                <form id="sched_form" novalidate>
                  <input type="text" name="company" class="sr-only" tabindex="-1" aria-hidden="true">

                  <div class="row row--2">
                    <div><label for="firstName">First name</label><input id="firstName" name="firstName" required placeholder="John"></div>
                    <div><label for="lastName">Last name</label><input id="lastName" name="lastName" required placeholder="Doe"></div>
                  </div>
                  <div class="row row--2">
                    <div><label for="email">Email</label><input id="email" name="email" type="email" required placeholder="john@example.com"></div>
                    <div><label for="phone">Phone ${this.requirePhone ? "(required)" : "(optional)"} </label><input id="phone" name="phone" type="tel" ${this.requirePhone ? "required" : ""} placeholder="(555) 123-4567"></div>
                  </div>
                  <div class="row row--2">
                    <div>
                      <label for="referral">How did you hear about us?</label>
                      <select id="referral" name="referral" required>
                        <option value="">Select an option</option>
                        <option>Instagram</option>
                        <option>Google Search</option>
                        <option>Friend or Family</option>
                        <option>TikTok</option>
                        <option>Facebook</option>
                        <option>Walk-in</option>
                        <option>Referral</option>
                      </select>
                    </div>
                    <div></div>
                  </div>

                  <div>
                    <label for="message">Tell us about your vision</label>
                    <textarea id="message" name="message" placeholder="Style, metal, stones, budget range, timeline… links welcome."></textarea>
                  </div>

                  <div class="actions">
                    <button class="btn btn--ghost js-back" type="button">Back</button>
                    <button class="btn btn--fill" type="submit">Request Appointment</button>
                  </div>

                  <div id="status" class="status" hidden></div>
                </form>
              </section>
            </div>
          </div>
        </div>
      `;

      this.shadowRoot.innerHTML = `
        ${styles}
        <div class="wrap">
          <section class="hero">
            ${heroLogo}
            <h1>Private Consultation</h1>
            <p>From inspiration to reality. 3D rendering provided once you have committed to your center stone.</p>
            <button class="cta js-open" type="button">Book an Appointment</button>
          </section>
          ${timeline}
          ${sheet}
        </div>
      `;
    }

    /* ---------- behavior ---------- */
    setupScheduler() {
      const $  = (sel, r=this.shadowRoot) => r.querySelector(sel);
      const $$ = (sel, r=this.shadowRoot) => Array.from(r.querySelectorAll(sel));

      const sheet   = $("#scheduler");
      const openBtn = $(".js-open");
      const backdrop= $(".backdrop", sheet);
      const closeBtn= $("[data-close]", sheet);

      // Date min = today
      const dateEl = $("#sched_date", sheet);
      if (dateEl) dateEl.min = new Date().toISOString().split("T")[0];

      const open = () => {
        this._open = true;
        this._lastFocus = this.shadowRoot.activeElement || document.activeElement;
        sheet.setAttribute("open", "");
        this.goStep(1);
        setTimeout(() => sheet.querySelector(".loc")?.focus(), 40);
      };
      const close = () => {
        this._open = false;
        sheet.removeAttribute("open");
        this._lastFocus && this._lastFocus.focus?.();
      };

      openBtn?.addEventListener("click", open);
      backdrop?.addEventListener("click", close);
      closeBtn?.addEventListener("click", close);
      this.shadowRoot.addEventListener("keydown", (e) => { if (this._open && e.key === "Escape") close(); });

      // Step navigation
      $$("[data-step]", sheet).forEach(p => {
        const k = +p.dataset.step;
        p.querySelector(".js-next")?.addEventListener("click", () => this.goStep(k + 1));
        p.querySelector(".js-back")?.addEventListener("click",  () => this.goStep(k - 1));
      });

      // Location
      $$(".loc", sheet).forEach(btn => {
        btn.addEventListener("click", () => {
          $$(".loc", sheet).forEach(b => b.setAttribute("aria-pressed", "false"));
          btn.setAttribute("aria-pressed", "true");
          this.state.location = btn.dataset.loc;
        });
      });

      // Service
      let chosenSvc = null;
      const step2 = $('[data-step="2"]', sheet);
      $$(".svc", sheet).forEach(btn => {
        btn.addEventListener("click", () => {
          chosenSvc?.classList.remove("selected");
          chosenSvc?.setAttribute("aria-pressed", "false");
          chosenSvc = btn;
          btn.classList.add("selected");
          btn.setAttribute("aria-pressed", "true");

          this.state.service = btn.dataset.svc || "";
          this.state.durationText = btn.dataset.dur || "";
          step2.querySelector(".js-next").disabled = !this.state.service;
        });
      });

      // Date & time
      const next3 = $('[data-step="3"] .js-next', sheet);
      const slots = $$(".slot", sheet);
      const setTime = (v) => {
        this.state.time = v || "";
        slots.forEach(b => b.classList.toggle("selected", b.dataset.time === v));
      };
      const enable3 = () => { next3.disabled = !(this.state.date && this.state.time); };

      slots.forEach(b => {
        b.addEventListener("click", () => { setTime(b.dataset.time); enable3(); });
        b.addEventListener("keydown", e => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault(); setTime(b.dataset.time); enable3();
          }
        });
      });
      dateEl?.addEventListener("input", () => { this.state.date = dateEl.value; enable3(); });

      // Confirm
      this.onEnterStep4 = () => {
        $(".sum-loc", sheet).textContent = this.state.location || "—";
        $(".sum-svc", sheet).textContent = this.state.service
          ? `${this.state.service}${this.state.durationText ? ` (${this.state.durationText})` : ""}` : "—";
        $(".sum-dt", sheet).textContent = (this.state.date && this.state.time)
          ? `${this.state.date} at ${prettyTime(this.state.time)}` : "—";
      };

      const form = $("#sched_form", sheet);
      form?.addEventListener("submit", this.handleSubmit.bind(this));
    }

    goStep(k) {
      const sheet = this.shadowRoot.getElementById("scheduler");
      const panes = Array.from(sheet.querySelectorAll(".pane"));
      const steps = Array.from(sheet.querySelectorAll(".stepper__item"));
      if (k < 1) k = 1; if (k > 4) k = 4;
      this.state.step = k;
      panes.forEach(p => p.toggleAttribute("active", +p.dataset.step === k));
      steps.forEach(s => s.classList.toggle("active", +s.dataset.k <= k));
      if (k === 4 && this.onEnterStep4) this.onEnterStep4();
      if (k === 2) sheet.querySelector('[data-step="2"] .js-next').disabled = !this.state.service;
      if (k === 3) sheet.querySelector('[data-step="3"] .js-next').disabled = !(this.state.date && this.state.time);
    }

    async handleSubmit(e) {
      e.preventDefault();
      const form = e.target;
      const fd = new FormData(form);

      // honeypot
      if ((fd.get("company") || "").toString().trim()) return;

      const firstName = (fd.get("firstName") || "").toString().trim();
      const lastName  = (fd.get("lastName")  || "").toString().trim();
      const email     = (fd.get("email")     || "").toString().trim();
      const phone     = (fd.get("phone")     || "").toString().trim();
      const message   = (fd.get("message")   || "").toString().trim();
      const referral  = (fd.get("referral")  || "").toString().trim();

      if (!firstName || !lastName || !email || !referral) {
        this._setStatus("Please complete all required fields.", true);
        return;
      }
      if (!this.state.service || !this.state.date || !this.state.time) {
        this._setStatus("Please pick service, date, and time.", true);
        return;
      }

      const payload = {
        firstName, lastName, email, phone, message, referral,
        service: this.state.service, date: this.state.date, time: this.state.time,
        location: this.state.location, duration: this.state.durationText
      };

      try {
        const btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = true; btn.textContent = "Submitting…"; }

        // AJAX to Shopify contact endpoint
        await this._ajaxToContact(payload);

        form.reset?.();
        this._setStatus(this.successText, false);
      } catch (err) {
        console.error(err);
        // Generic error (reCAPTCHA or CSP can also block)
        this._setStatus(this.errorText, true);
      } finally {
        const btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.disabled = false; btn.textContent = "Request Appointment"; }
      }
    }

    // Always POST to /contact (Shopify contact endpoint). Do NOT POST to the page URL.
    async _ajaxToContact(payload) {
      const params = new URLSearchParams();
      params.set("form_type", "contact");
      params.set("utf8", "✓");

      const fullName = [payload.firstName, payload.lastName].filter(Boolean).join(" ").trim();
      const timePretty = payload.time ? prettyTime(payload.time) : "";

      params.set("contact[name]", fullName);
      params.set("contact[email]", payload.email || "");
      if (payload.phone)    params.set("contact[phone]", payload.phone);
      if (payload.service)  params.set("contact[service]", payload.service);
      if (payload.date)     params.set("contact[date]", payload.date);
      if (timePretty)       params.set("contact[time]", timePretty); // <- send 12-hour time
      if (payload.referral) params.set("contact[referral]", payload.referral);

      params.set(
        "contact[body]",
        `Location: ${payload.location || "—"}\n` +
        `Service: ${payload.service || "—"} ${payload.duration ? `(${payload.duration})` : ""}\n` +
        `Date: ${payload.date || "—"}  Time: ${timePretty || "—"}\n` + // <- 12-hour in email body
        `Referral: ${payload.referral || "—"}\n` +
        `Phone: ${payload.phone || "—"}\n\n` +
        `Message:\n${payload.message || "—"}`
      );

      const res = await fetch("/contact", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params
      });

      // If Shopify blocks AJAX (reCAPTCHA/CSP), this may be !ok (e.g., 400).
      if (!res.ok) throw new Error(`Contact POST failed: ${res.status}`);
      return { ok: true };
    }

    _setStatus(msg, isErr) {
      const el = this.shadowRoot.getElementById("status");
      if (!el) return;
      el.hidden = false;
      el.textContent = msg;
      el.className = `status ${isErr ? "err" : "ok"}`;
    }
  }

  customElements.define("x-appointment-timeline", AppointmentTimeline);
})();
